import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BadgeCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Link2,
  MessageSquareText,
  Repeat,
  Search,
  UserPlus,
  Users,
} from "lucide-react";
import { UserContext } from "../context/UserContext";
import { clubErrorMessage, getClubCustomers } from "../api/club";
import { getAllReviews } from "../api/reviews";
import DashboardKpiCard from "../components/dashboard/DashboardKpiCard";
import AddCustomerDialog from "../components/customer-club/AddCustomerDialog";
import CustomerDetailPanel from "../components/customer-club/CustomerDetailPanel";
import ReviewsPanel from "../components/customer-club/ReviewsPanel";
import {
  SEGMENTS,
  SEGMENT_HINTS,
  SOURCE_LABELS,
  customersToCsv,
  formatLastVisit,
  formatPhone,
  formatToman,
  segmentOf,
  toFa,
} from "../components/customer-club/clubFormat";

const PAGE_SIZE = 25;

const SORTS = [
  { value: "recent", label: "جدیدترین عضو" },
  { value: "lastVisit", label: "آخرین مراجعه" },
  { value: "visits", label: "بیشترین مراجعه" },
  { value: "spent", label: "بیشترین خرید" },
];

const sorters = {
  recent: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  lastVisit: (a, b) => new Date(b.stats.lastVisitAt || 0) - new Date(a.stats.lastVisitAt || 0),
  visits: (a, b) => b.stats.visitCount - a.stats.visitCount,
  spent: (a, b) => b.stats.totalSpent - a.stats.totalSpent,
};

// Persian/Arabic digits → ASCII so "۰۹۱۲" matches a stored "0912".
const toAsciiDigits = (value) =>
  value.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d)).replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const summarize = (customers) => {
  const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  return {
    total: customers.length,
    newThisMonth: customers.filter((c) => new Date(c.createdAt).getTime() >= monthAgo).length,
    returning: customers.filter((c) => c.stats.visitCount >= 2).length,
    marketingConsent: customers.filter((c) => c.marketingConsent).length,
  };
};

const SegmentLabel = ({ value }) => {
  const segment = segmentOf(value);
  return (
    <span className={`text-xs font-bold ${segment.className}`} title={SEGMENT_HINTS[value]}>
      {segment.label}
    </span>
  );
};

const HeaderButton = ({ icon: Icon, children, primary = false, ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-bold transition-colors focus:outline-none focus:ring-4 focus:ring-primary/15 disabled:opacity-50 ${
      primary
        ? "bg-primary text-white hover:bg-[#e8731f]"
        : "border border-gray-200 bg-white text-accent hover:border-primary/40 hover:bg-gray-50"
    }`}
  >
    <Icon size={16} aria-hidden="true" />
    {children}
  </button>
);

function EmptyClub({ joinLink, onCopy, copied, onAdd }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary_1 text-primary">
        <Users size={20} aria-hidden="true" />
      </span>
      <h2 className="mt-3 text-base font-extrabold text-accent">باشگاه هنوز عضوی ندارد</h2>
      <p className="mt-1.5 max-w-md text-sm leading-6 text-gray-500">
        مشتری‌ها به‌صورت خودکار از رزروها، نظرهای تأییدشده و فرم عضویت منوی آنلاین اضافه می‌شوند.
        لینک عضویت را روی میزها یا در شبکه‌های اجتماعی بگذارید.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {joinLink && (
          <HeaderButton icon={copied ? Check : Link2} onClick={onCopy}>
            {copied ? "کپی شد" : "کپی لینک عضویت"}
          </HeaderButton>
        )}
        <HeaderButton icon={UserPlus} primary onClick={onAdd}>
          افزودن دستی
        </HeaderButton>
      </div>
    </div>
  );
}

export default function CustomerClub() {
  const { user } = useContext(UserContext);
  const restaurantId = user?._id;
  const joinLink = user?.menuLink ? `${window.location.origin}/menu/${user.menuLink}/club` : "";

  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState("");

  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState("all");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadCustomers = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getClubCustomers(restaurantId);
      setCustomers(data.customers);
    } catch (loadError) {
      setError(clubErrorMessage(loadError, "دریافت مشتریان ناموفق بود."));
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const loadReviews = useCallback(async () => {
    if (!restaurantId) return;
    setReviewsLoading(true);
    setReviewsError("");
    try {
      setReviews(await getAllReviews(restaurantId));
    } catch {
      setReviewsError("دریافت نظرات ناموفق بود.");
    } finally {
      setReviewsLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadCustomers();
    loadReviews();
  }, [loadCustomers, loadReviews]);

  useEffect(() => setPage(1), [search, segment, sort]);

  const summary = useMemo(() => summarize(customers), [customers]);
  const segmentCounts = useMemo(
    () => customers.reduce((acc, c) => ({ ...acc, [c.segment]: (acc[c.segment] || 0) + 1 }), {}),
    [customers]
  );

  const filtered = useMemo(() => {
    const query = toAsciiDigits(search.trim().toLowerCase());
    return customers
      .filter((c) => segment === "all" || c.segment === segment)
      .filter((c) => {
        if (!query) return true;
        return (
          (c.name || "").toLowerCase().includes(query) ||
          c.phone.includes(query.replace(/\s/g, "")) ||
          (c.tags || []).some((tag) => tag.toLowerCase().includes(query))
        );
      })
      .sort(sorters[sort]);
  }, [customers, search, segment, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pendingReviews = reviews.filter((r) => r.status === "pending").length;

  const copyJoinLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("لینک عضویت را کپی کنید:", joinLink);
    }
  };

  const exportCsv = () => {
    const blob = new Blob([customersToCsv(filtered)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const replaceCustomer = (updated) =>
    setCustomers((current) => current.map((c) => (c._id === updated._id ? updated : c)));

  return (
    <main className="mx-auto max-w-[90rem] space-y-4 pb-6" dir="rtl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-accent">باشگاه مشتریان</h1>
          <p className="mt-0.5 text-sm text-gray-500">مشتری‌هایتان را بشناسید و دوباره به رستوران برگردانید</p>
        </div>
        {tab === "customers" && (
          <div className="flex flex-wrap items-center gap-2">
            {joinLink && (
              <HeaderButton icon={copied ? Check : Link2} onClick={copyJoinLink} title={joinLink}>
                {copied ? "کپی شد" : "لینک عضویت"}
              </HeaderButton>
            )}
            <HeaderButton icon={Download} onClick={exportCsv} disabled={!filtered.length}>
              خروجی اکسل
            </HeaderButton>
            <HeaderButton icon={UserPlus} primary onClick={() => setAdding(true)}>
              افزودن مشتری
            </HeaderButton>
          </div>
        )}
      </header>

      <nav className="flex gap-5 border-b border-gray-200" role="tablist" aria-label="بخش‌های باشگاه">
        {[
          { value: "customers", label: "مشتریان", count: summary.total },
          { value: "reviews", label: "نظرات", count: pendingReviews, attention: pendingReviews > 0 },
        ].map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={tab === item.value}
            onClick={() => setTab(item.value)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-sm font-bold transition-colors ${
              tab === item.value ? "border-primary text-accent" : "border-transparent text-gray-500 hover:text-accent"
            }`}
          >
            {item.label}
            {item.count > 0 && (
              <span className={`rounded-full px-1.5 text-[11px] leading-5 ${item.attention ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`}>
                {toFa(item.count)}
              </span>
            )}
          </button>
        ))}
      </nav>

      {tab === "reviews" ? (
        <ReviewsPanel
          reviews={reviews}
          loading={reviewsLoading}
          error={reviewsError}
          onChanged={(updated) => {
            setReviews((current) => current.map((r) => (r._id === updated._id ? updated : r)));
            // An approved review with a phone may have added a member.
            if (updated.status === "approved" && updated.phone) loadCustomers();
          }}
        />
      ) : (
        <>
          <section aria-label="آمار باشگاه" className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 md:grid-cols-4">
            <DashboardKpiCard icon={Users} label="کل اعضا" value={toFa(summary.total)} secondary="مشتری ثبت‌شده" />
            <DashboardKpiCard icon={UserPlus} label="عضو جدید" value={toFa(summary.newThisMonth)} secondary="در ۳۰ روز اخیر" />
            <DashboardKpiCard icon={Repeat} label="مشتری بازگشتی" value={toFa(summary.returning)} secondary="دو مراجعه یا بیشتر" />
            <DashboardKpiCard icon={MessageSquareText} label="رضایت پیامک" value={toFa(summary.marketingConsent)} secondary="قابل ارسال پیام تبلیغاتی" />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white" aria-label="فهرست مشتریان">
            <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
              <label className="relative min-w-[14rem] flex-1">
                <span className="sr-only">جستجو</span>
                <Search size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="جستجوی نام، شماره یا برچسب"
                  className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-3 pr-9 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                مرتب‌سازی
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:border-primary focus:outline-none"
                >
                  {SORTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-gray-100 px-3 py-2" role="tablist" aria-label="گروه مشتریان">
              {[{ value: "all", label: "همه" }, ...SEGMENTS].map((item) => {
                const count = item.value === "all" ? customers.length : segmentCounts[item.value] || 0;
                return (
                  <button
                    key={item.value}
                    type="button"
                    role="tab"
                    aria-selected={segment === item.value}
                    onClick={() => setSegment(item.value)}
                    title={SEGMENT_HINTS[item.value]}
                    className={`min-h-8 rounded-lg px-2.5 text-xs font-bold transition-colors ${
                      segment === item.value ? "bg-gray-100 text-accent" : "text-gray-500 hover:text-accent"
                    }`}
                  >
                    {item.label} <span className="font-medium text-gray-400">{toFa(count)}</span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="space-y-2 p-4" aria-busy="true" aria-label="در حال بارگذاری مشتریان">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-gray-100" />)}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-2 p-10 text-center">
                <AlertCircle size={22} className="text-red-600" aria-hidden="true" />
                <p className="text-sm font-bold text-red-800">{error}</p>
                <button type="button" onClick={loadCustomers} className="text-xs font-bold text-red-700 underline">تلاش دوباره</button>
              </div>
            ) : customers.length === 0 ? (
              <EmptyClub joinLink={joinLink} onCopy={copyJoinLink} copied={copied} onAdd={() => setAdding(true)} />
            ) : filtered.length === 0 ? (
              <p className="p-10 text-center text-sm text-gray-500">مشتری‌ای با این مشخصات پیدا نشد.</p>
            ) : (
              <>
                {/* Desktop table */}
                <table className="hidden w-full text-sm lg:table">
                  <thead>
                    <tr className="border-b border-gray-100 text-right text-xs font-bold text-gray-400">
                      <th className="px-4 py-2.5 font-bold">مشتری</th>
                      <th className="px-3 py-2.5 font-bold">گروه</th>
                      <th className="px-3 py-2.5 font-bold">مراجعه</th>
                      <th className="px-3 py-2.5 font-bold">آخرین مراجعه</th>
                      <th className="px-3 py-2.5 font-bold">مجموع خرید</th>
                      <th className="px-3 py-2.5 font-bold">منبع</th>
                      <th className="w-8 px-3 py-2.5"><span className="sr-only">جزئیات</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visible.map((c) => (
                      <tr
                        key={c._id}
                        onClick={() => setSelectedId(c._id)}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                      >
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); setSelectedId(c._id); }}
                            className="text-right focus:outline-none focus-visible:underline"
                          >
                            <span className="flex items-center gap-1.5 font-bold text-accent">
                              {c.name || "بدون نام"}
                              {c.phoneVerified && <BadgeCheck size={14} className="text-secondary" aria-label="شماره تأیید شده" />}
                            </span>
                            <span className="text-xs text-gray-500" dir="ltr">{formatPhone(c.phone)}</span>
                          </button>
                        </td>
                        <td className="px-3 py-2.5"><SegmentLabel value={c.segment} /></td>
                        <td className="px-3 py-2.5 font-semibold text-gray-700">{toFa(c.stats.visitCount)}</td>
                        <td className="px-3 py-2.5 text-gray-600">{formatLastVisit(c.stats.lastVisitAt)}</td>
                        <td className="px-3 py-2.5 text-gray-600">{c.stats.totalSpent ? formatToman(c.stats.totalSpent) : "—"}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500">{SOURCE_LABELS[c.source] || c.source}</td>
                        <td className="px-3 py-2.5"><ChevronLeft size={16} className="text-gray-300" aria-hidden="true" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile list */}
                <ul className="divide-y divide-gray-100 lg:hidden">
                  {visible.map((c) => (
                    <li key={c._id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(c._id)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-right hover:bg-gray-50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1.5 truncate font-bold text-accent">
                            {c.name || "بدون نام"}
                            {c.phoneVerified && <BadgeCheck size={14} className="shrink-0 text-secondary" aria-hidden="true" />}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            <span dir="ltr">{formatPhone(c.phone)}</span> · {toFa(c.stats.visitCount)} مراجعه · {formatLastVisit(c.stats.lastVisitAt)}
                          </p>
                        </div>
                        <SegmentLabel value={c.segment} />
                        <ChevronLeft size={16} className="shrink-0 text-gray-300" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>

                {pageCount > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500">
                    <span>
                      {toFa((page - 1) * PAGE_SIZE + 1)}–{toFa(Math.min(page * PAGE_SIZE, filtered.length))} از {toFa(filtered.length)}
                    </span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="rounded-lg border border-gray-200 p-1.5 hover:bg-gray-50 disabled:opacity-40"
                        aria-label="صفحه قبل"
                      >
                        <ChevronRight size={15} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page === pageCount}
                        className="rounded-lg border border-gray-200 p-1.5 hover:bg-gray-50 disabled:opacity-40"
                        aria-label="صفحه بعد"
                      >
                        <ChevronLeft size={15} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {adding && (
        <AddCustomerDialog
          restaurantId={restaurantId}
          onClose={() => setAdding(false)}
          onCreated={(customer) => {
            setCustomers((current) => [customer, ...current]);
            setAdding(false);
            setSelectedId(customer._id);
          }}
        />
      )}

      {selectedId && (
        <CustomerDetailPanel
          restaurantId={restaurantId}
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={replaceCustomer}
          onDeleted={(id) => {
            setCustomers((current) => current.filter((c) => c._id !== id));
            setSelectedId(null);
          }}
        />
      )}
    </main>
  );
}
